/**
 * ══════════════════════════════════════════════════════════════
 *  MÓDULO DE SEGURIDAD — Presupuesto Pro
 *  Implementa: Anti-brute-force, sanitización XSS, validación
 *  de importaciones, fingerprinting de sesión y self-XSS warning.
 * ══════════════════════════════════════════════════════════════
 */

// ── Constantes ────────────────────────────────────────────────────────────────
const FAIL_KEY      = 'pp_auth_fail';   // nº de intentos fallidos
const LOCK_KEY      = 'pp_auth_lock';   // timestamp fin de bloqueo
const MAX_ATTEMPTS  = 5;                // intentos antes de bloquear
const LOCKOUT_MS    = 15 * 60 * 1000;  // 15 minutos de bloqueo
const MAX_INPUT_LEN = 100_000;          // longitud máx. por campo de texto (excluye data URLs)

// ── Aviso anti-ingeniería social (Self-XSS) ───────────────────────────────────
export function printSecurityWarning(): void {
  try {
    console.log(
      '%c⛔ ADVERTENCIA DE SEGURIDAD',
      'color:#ff4444;font-size:20px;font-weight:bold;'
    );
    console.log(
      '%cNo pegues ningún código en esta consola. Los atacantes pueden usar esto para robar tus datos (ataque Self-XSS).',
      'color:#ff8800;font-size:13px;'
    );
    console.log(
      '%cPRESUPUESTO PRO — Solo para uso autorizado.',
      'color:#888;font-size:11px;'
    );
  } catch { /* silencioso si console no disponible */ }
}

// ── Protección contra fuerza bruta ───────────────────────────────────────────
export function getLoginAttempts(): number {
  return parseInt(localStorage.getItem(FAIL_KEY) ?? '0', 10);
}

export function getLockoutUntil(): number {
  return parseInt(localStorage.getItem(LOCK_KEY) ?? '0', 10);
}

export function isLockedOut(): boolean {
  const until = getLockoutUntil();
  if (until <= 0) return false;
  if (Date.now() < until) return true;
  // Lockout expirado: limpiar
  localStorage.removeItem(LOCK_KEY);
  localStorage.removeItem(FAIL_KEY);
  return false;
}

export function getLockoutRemainingMs(): number {
  return Math.max(0, getLockoutUntil() - Date.now());
}

export function recordFailedAttempt(): { attempts: number; locked: boolean } {
  const attempts = getLoginAttempts() + 1;
  localStorage.setItem(FAIL_KEY, String(attempts));
  if (attempts >= MAX_ATTEMPTS) {
    localStorage.setItem(LOCK_KEY, String(Date.now() + LOCKOUT_MS));
    return { attempts, locked: true };
  }
  return { attempts, locked: false };
}

export function clearLoginAttempts(): void {
  localStorage.removeItem(FAIL_KEY);
  localStorage.removeItem(LOCK_KEY);
}

/** Retraso progresivo exponencial entre intentos: 0.5s, 1s, 2s, 4s, 8s */
export function loginDelay(attempts: number): Promise<void> {
  const ms = attempts <= 1 ? 0 : Math.min(Math.pow(2, attempts - 2) * 500, 8000);
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ── Fingerprint de navegador (vinculación de sesión) ─────────────────────────
export async function getBrowserFingerprint(): Promise<string> {
  const raw = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency ?? 0,
  ].join('||');
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 24); // 24 hex chars = 96 bits — suficiente para fingerprint
  } catch {
    return 'fp-fallback';
  }
}

// ── Sanitización de entradas (prevención XSS) ─────────────────────────────────
/**
 * Elimina etiquetas HTML, scripts y atributos de evento de un string.
 * Trunca a MAX_INPUT_LEN para prevenir ataques de payload gigante.
 */
export function sanitizeText(input: unknown): string {
  if (typeof input !== 'string') return String(input ?? '');
  return input
    .slice(0, MAX_INPUT_LEN)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
    .replace(/<object[\s\S]*?>[\s\S]*?<\/object>/gi, '')
    .replace(/<embed[^>]*>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*\S+/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/vbscript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
    .replace(/<[^>]{0,500}>/g, '')
    .trim();
}

/**
 * Sanitiza recursivamente todos los campos string de un objeto.
 * Las data URLs (logos, imágenes en base64) se excluyen de la sanitización:
 * su contenido es binario codificado en base64 y no puede contener XSS.
 */
export function sanitizeObject<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) {
    return (obj as unknown[]).map(item => sanitizeObject(item)) as unknown as T;
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof value === 'string') {
      // Las data URLs (imágenes base64) no necesitan ni admiten sanitización de texto
      if (value.startsWith('data:image/') || value.startsWith('data:application/octet-stream')) {
        result[key] = value; // conservar intacta
      } else {
        result[key] = sanitizeText(value);
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = sanitizeObject(value);
    } else {
      result[key] = value;
    }
  }
  return result as T;
}

// ── Sanitización para inyección en ventana PDF ────────────────────────────────
/**
 * Sanitiza el HTML que se inyecta en la ventana de impresión.
 * Elimina scripts y manejadores de eventos, permite HTML estructural de maquetación.
 */
export function sanitizePdfHtml(html: string): string {
  return html
    // Eliminar bloques script completos
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    // Eliminar iframes
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
    // Eliminar manejadores de evento inline con valor entre comillas: onXXX="..."
    // EXCLUYE src="data:..." y href="..." para no corromper imágenes o enlaces
    .replace(/\s(on[a-zA-Z]+)\s*=\s*"[^"]*"/gi, '')
    .replace(/\s(on[a-zA-Z]+)\s*=\s*'[^']*'/gi, '')
    // Eliminar URLs javascript:
    .replace(/href\s*=\s*["']?\s*javascript\s*:/gi, 'href="#"')
    .replace(/src\s*=\s*["']?\s*javascript\s*:/gi, 'src="#"');
}

// ── Validación estricta de backups importados ─────────────────────────────────
export interface ImportedBackup {
  version: number;
  exportedAt?: string;
  presupuestos: unknown[];
  clientes: unknown[];
  productos: unknown[];
  config: unknown;
  counter?: string;
}

export function validateBackupData(data: unknown): { valid: boolean; error?: string } {
  if (typeof data !== 'object' || data === null) {
    return { valid: false, error: 'El archivo no contiene datos válidos.' };
  }
  const d = data as Record<string, unknown>;

  // Versión
  if (d.version !== 1 && d.version !== '1') {
    return { valid: false, error: `Versión de backup no reconocida (${d.version}). Solo se admite versión 1.` };
  }

  // Arrays obligatorios
  for (const key of ['presupuestos', 'clientes', 'productos']) {
    if (!Array.isArray(d[key])) {
      return { valid: false, error: `El campo "${key}" debe ser una lista. El archivo parece corrupto.` };
    }
  }

  // Tamaño máximo razonable (anti-DOS en import)
  const str = JSON.stringify(data);
  if (str.length > 50 * 1024 * 1024) { // 50 MB
    return { valid: false, error: 'El archivo de backup supera el tamaño máximo permitido (50 MB).' };
  }

  // Validar que config sea un objeto
  if (typeof d.config !== 'object' || d.config === null) {
    return { valid: false, error: 'El campo "config" no tiene el formato esperado.' };
  }

  return { valid: true };
}

// ── Integridad de sesión (checksum simple de datos críticos) ──────────────────
export async function generateIntegrityToken(data: string): Promise<string> {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data + 'pp-integrity-salt-v1'));
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch {
    return '';
  }
}
